'use client';

import { AtSign, AudioLines, Clock3, ImageIcon, Plus, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type CSSProperties, useEffect, useState } from 'react';
import { ProductLineImage } from '@/app/components/icons/ProductLineImage';
import { ProductLineSubject } from '@/app/components/icons/ProductLineSubject';
import { ReferenceMaterial } from '@/app/components/icons/ReferenceMaterial';
import { HeadlessImageUploader } from '@/app/components/shared/ImageUploader';
import { HeadlessMediaUploader } from '@/app/components/shared/MediaUploader/HeadlessMediaUploader';
import { TransitionDurationSelect } from '@/app/components/shared/TransitionDurationSelect';
import { HeadlessVideoUploader } from '@/app/components/shared/VideoUploader/HeadlessVideoUploader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import type { ModelVersion } from '@/app/types';
import { cn } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import {
  getDesktopMediaStackExpandedWidth,
  HomeGenerationDesktopMediaArea,
  HomeGenerationMobileUploadTrigger,
  HomeGenerationMultiFrameFrameCard,
  HomeGenerationMultiFrameTransitionCard,
  MobileMediaChipItem,
} from './HomeGenerationFormMedia';
import type { HomeGenerationFormController } from './hooks';
import { getHomeMultiFrameDurationLabel, getHomeMultiFramePrompt, HOME_MULTI_FRAME_DEFAULT_DURATION } from './utils';

interface HomeGenerationMediaProps {
  controller: HomeGenerationFormController;
}

const DESKTOP_REFERENCE_STACK_COLLAPSED_WIDTH = 64;
const DESKTOP_REFERENCE_STACK_GAP = 12;
const DESKTOP_REFERENCE_DIVIDER_WIDTH = 1;
const DESKTOP_REFERENCE_EXPANDED_SECTION_GAP = 12;

const getReferenceUploadEmptyIcon = ({
  canUploadImage,
  canUploadVideo,
  canUploadAudio = false,
}: {
  canUploadImage: boolean;
  canUploadVideo: boolean;
  canUploadAudio?: boolean;
}) => {
  const iconClassName = 'size-5';

  if (canUploadImage && canUploadVideo) {
    return (
      <span className="flex items-center gap-1" aria-hidden="true">
        <ProductLineImage className={iconClassName} data-media-type="image" />
        <span aria-hidden="true" className="h-3 w-px shrink-0 bg-system-white[.24]" />
        <Video className={iconClassName} data-media-type="video" />
      </span>
    );
  }

  if (canUploadImage && canUploadAudio) {
    return (
      <span className="flex items-center gap-1" aria-hidden="true">
        <ProductLineImage className={iconClassName} data-media-type="image" />
        <span aria-hidden="true" className="h-3 w-px shrink-0 bg-system-white[.24]" />
        <AudioLines className={iconClassName} data-media-type="audio" />
      </span>
    );
  }

  return canUploadVideo ? (
    <Video className={iconClassName} data-media-type="video" aria-hidden="true" />
  ) : canUploadAudio ? (
    <AudioLines className={iconClassName} data-media-type="audio" aria-hidden="true" />
  ) : (
    <ProductLineImage className={iconClassName} data-media-type="image" aria-hidden="true" />
  );
};

export const HomeGenerationMobileMediaStrip = ({ controller }: HomeGenerationMediaProps) => {
  const { media, multiFrame } = controller;

  if (multiFrame.isMultiFrameMode) {
    return (
      <div className="hide-scrollbar flex min-w-0 gap-2 overflow-x-auto">
        <HomeGenerationMultiFrameRail controller={controller} compact={true} wrap={false} />
      </div>
    );
  }

  if (!media.showMobileMediaRow) {
    return null;
  }

  return (
    <div className="hide-scrollbar flex min-w-0 gap-2 overflow-x-auto">
      {media.mediaChips.map((chip) => (
        <MobileMediaChipItem key={chip.id} chip={chip} />
      ))}
      <HomeGenerationMobileUploadTriggerSlot controller={controller} />
    </div>
  );
};

export const HomeGenerationMobileUploadTriggerSlot = ({ controller }: HomeGenerationMediaProps) => {
  const { media } = controller;

  if (media.availableMobileMediaInputs.length > 1) {
    return <HomeGenerationMobileUploadTrigger onClick={media.handleOpenMobileMediaOptions} tone="light" />;
  }

  if (media.showMobileImageInput) {
    if (media.shouldUseMixedReferenceMediaUpload) {
      return (
        <HeadlessMediaUploader
          maxCount={media.mixedReferenceMediaMaxCount}
          maxImageCount={media.remainingImageUploadCount}
          maxVideoCount={media.canUploadReferenceVideo ? media.referenceVideoConfig.maxVideoCount : 0}
          disabled={!media.canUploadMixedReferenceMedia}
          acceptImage={media.remainingImageUploadCount > 0}
          acceptVideo={media.canUploadReferenceVideo}
          getSelectedImage={(url) =>
            resizeSourceImage({
              url,
              maxImageSize: media.imageUploadMaxImageSize,
            })
          }
          minVideoDuration={media.referenceVideoConfig.minVideoDuration}
          maxVideoDuration={media.referenceVideoConfig.maxVideoDuration}
          maxVideoSize={media.referenceVideoConfig.maxVideoSize}
          captureCoverImage={true}
          uploadCoverImage={true}
          allowTempMode={true}
          emitUploadingPreview={true}
          onChange={media.handleMobileMediaOptionMediaChange}
        >
          {({ openFileSelector }) => (
            <HomeGenerationMobileUploadTrigger
              onClick={() => {
                setTimeout(() => {
                  void openFileSelector();
                }, 0);
              }}
              disabled={!media.canUploadMixedReferenceMedia}
              tone="light"
            />
          )}
        </HeadlessMediaUploader>
      );
    }

    return (
      <HeadlessImageUploader
        maxCount={media.remainingImageUploadCount}
        disabled={media.remainingImageUploadCount <= 0}
        getSelectedImage={(url) =>
          resizeSourceImage({
            url,
            maxImageSize: media.imageUploadMaxImageSize,
          })
        }
        onChange={media.handleUploadImage}
        allowTempMode={true}
        emitUploadingPreview={true}
      >
        {({ openFileSelector }) => (
          <HomeGenerationMobileUploadTrigger
            onClick={openFileSelector}
            disabled={media.remainingImageUploadCount <= 0}
            tone="light"
          />
        )}
      </HeadlessImageUploader>
    );
  }

  if (media.showMobileSubjectInput) {
    return <HomeGenerationMobileUploadTrigger onClick={() => media.handleSelectSubject()} tone="light" />;
  }

  return null;
};

export const HomeGenerationMultiFrameRail = ({
  compact = false,
  controller,
  wrap = true,
}: HomeGenerationMediaProps & {
  compact?: boolean;
  wrap?: boolean;
}) => {
  const t = useTranslations();
  const { form, media, multiFrame, refs } = controller;
  const frameChips = media.mediaChips.filter((chip) => chip.type === 'image');

  if (!multiFrame.isMultiFrameMode) {
    return null;
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-3', wrap ? 'flex-wrap' : 'w-max flex-nowrap')}>
      {frameChips.map((chip, index) => {
        if (index === frameChips.length - 1) {
          return <HomeGenerationMultiFrameFrameCard key={chip.id} chip={chip} index={index} compact={compact} />;
        }

        const transition = multiFrame.transitions?.[index];
        const prompt = getHomeMultiFramePrompt(transition);
        const isOpen = multiFrame.openIndex === index;
        const durationValue = transition?.duration ?? HOME_MULTI_FRAME_DEFAULT_DURATION;
        const durationLabel = getHomeMultiFrameDurationLabel(transition);

        return (
          <div key={chip.id} className="flex items-center gap-3">
            <HomeGenerationMultiFrameFrameCard chip={chip} index={index} compact={compact} />
            <HomeGenerationMultiFrameTransitionCard
              key={`${transition?.from ?? index}-${transition?.to ?? index + 1}`}
              open={isOpen}
              onOpenChange={(open) => {
                multiFrame.setOpenIndex(open ? index : null);
                if (open) {
                  multiFrame.setDraft(prompt);
                }
              }}
              compact={compact}
              frameLabel={t('create.img2VideoForm.transition.label', { from: index + 1, to: index + 2 })}
              durationLabel={durationLabel}
              hasPrompt={!!prompt}
              prompt={prompt}
              promptDraft={multiFrame.draft}
              promptPlaceholder={t('create.img2VideoForm.transition.promptPlaceholder')}
              onPromptDraftChange={multiFrame.setDraft}
              onPromptConfirm={() => {
                multiFrame.updateTransitionPrompt(index, multiFrame.draft);
                multiFrame.setOpenIndex(null);
              }}
              durationSelect={
                <TransitionDurationSelect
                  isMultiFrameMode={true}
                  currentTransitionIndex={index}
                  value={durationValue}
                  onChange={(duration) => {
                    multiFrame.updateTransitionDuration(index, duration);
                  }}
                  settings={form.settings}
                  updateSettings={form.updateSettings}
                  onShouldUpgrade={(key) => {
                    multiFrame.handleTransitionUpgrade(key as string);
                  }}
                  modelVersion={form.modelVersion as ModelVersion}
                  type="img2video"
                  trigger={
                    <button
                      type="button"
                      className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full bg-white/[0.06] px-3 text-system-white64 text-xs transition-colors hover:bg-white/[0.08] hover:text-system-white"
                    >
                      <Clock3 className="size-3.5" />
                      {durationLabel}
                    </button>
                  }
                />
              }
              confirmLabel={t('common.dialog.confirm')}
              cancelLabel={t('common.dialog.cancel')}
              textareaRef={refs.multiFramePromptRef}
            />
          </div>
        );
      })}
      {media.remainingImageUploadCount > 0 ? (
        <HeadlessImageUploader
          maxCount={media.remainingImageUploadCount}
          disabled={media.remainingImageUploadCount <= 0}
          getSelectedImage={(url) =>
            resizeSourceImage({
              url,
              maxImageSize: media.imageUploadMaxImageSize,
            })
          }
          onChange={media.handleUploadImage}
          allowTempMode={true}
          emitUploadingPreview={true}
        >
          {({ openFileSelector }) => (
            <button
              type="button"
              className={cn(
                'flex shrink-0 items-center justify-center border border-white/[0.10] bg-white/[0.04] text-system-white48 transition-colors hover:border-white/[0.18] hover:bg-white/[0.06] hover:text-system-white',
                'h-[80px] w-[64px] rounded-12',
              )}
              onClick={openFileSelector}
            >
              <Plus className="size-5" />
            </button>
          )}
        </HeadlessImageUploader>
      ) : null}
    </div>
  );
};

export const HomeGenerationDesktopUploadArea = ({ controller }: HomeGenerationMediaProps) => {
  const t = useTranslations();
  const { media, multiFrame } = controller;
  const [referenceStacksExpanded, setReferenceStacksExpanded] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const allMediaChips = media.mediaChips ?? [];

  const imageChips = allMediaChips.filter((chip) => chip.type === 'image');
  const subjectChips = allMediaChips.filter((chip) => chip.type === 'element');
  const videoChips = allMediaChips.filter((chip) => chip.type === 'video');

  // Q2Pro 的卡片分类 (需要先定义，后续逻辑会用到)
  const q2ProVideoChips =
    media.videoMediaChips ??
    allMediaChips.filter(
      (chip) => chip.type === 'video' || (chip.type === 'element' && chip.elementModality === 'video'),
    );
  const q2ProImageChips = media.imageMediaChips ?? imageChips;
  const q2ProSubjectChips =
    media.subjectMediaChips ??
    allMediaChips.filter((chip) => chip.type === 'element' && chip.elementModality !== 'video');

  const canUploadImage = media.showImageInput && media.remainingImageUploadCount > 0;
  const canSelectSubject =
    media.showSubjectInput &&
    (controller.form.imageAndTextReferenceCount < controller.form.maxImageAndElementCount ||
      (media.shouldUseMixedReferenceMediaUpload && media.canUploadReferenceVideo));
  const shouldShowImageMediaArea = media.showImageInput || imageChips.length > 0;
  const shouldShowSubjectMediaArea = media.showSubjectInput || subjectChips.length > 0;
  const mediaUploadChips = media.shouldUseMixedReferenceMediaUpload
    ? allMediaChips.filter((chip) => chip.type !== 'element')
    : imageChips;
  const shouldUseReferenceStackRotation = media.isQ2ProSeparatedMediaAreas
    ? q2ProVideoChips.length > 0 || q2ProImageChips.length > 0 || q2ProSubjectChips.length > 0
    : shouldShowSubjectMediaArea && shouldShowImageMediaArea;
  const shouldRenderReferenceHoverBridge = media.isQ2ProSeparatedMediaAreas
    ? // Q2Pro: 只要有至少2个卡片就需要 hover bridge（覆盖所有间隙）
      (q2ProVideoChips.length > 0 ? 1 : 0) +
        (q2ProImageChips.length > 0 ? 1 : 0) +
        (q2ProSubjectChips.length > 0 ? 1 : 0) >=
      2
    : shouldUseReferenceStackRotation && mediaUploadChips.length > 0 && subjectChips.length > 0;

  // Q2Pro 的展开宽度计算
  const q2ProVideoExpandedWidth = media.isQ2ProSeparatedMediaAreas
    ? getDesktopMediaStackExpandedWidth(
        q2ProVideoChips.length,
        q2ProVideoChips.length > 0 && media.canUploadReferenceVideo,
      )
    : 0;
  const q2ProImageExpandedWidth = media.isQ2ProSeparatedMediaAreas
    ? getDesktopMediaStackExpandedWidth(q2ProImageChips.length, q2ProImageChips.length > 0 && canUploadImage)
    : 0;
  const q2ProSubjectExpandedWidth = media.isQ2ProSeparatedMediaAreas
    ? getDesktopMediaStackExpandedWidth(
        q2ProSubjectChips.length,
        q2ProSubjectChips.length > 0 &&
          controller.form.imageAndTextReferenceCount < controller.form.maxImageAndElementCount,
      )
    : 0;

  const imageExpandedStackWidth = media.isQ2ProSeparatedMediaAreas
    ? q2ProImageExpandedWidth
    : getDesktopMediaStackExpandedWidth(
        mediaUploadChips.length,
        mediaUploadChips.length > 1 &&
          !!(media.shouldUseMixedReferenceMediaUpload ? media.canUploadMixedReferenceMedia : canUploadImage),
      );
  // Q2Pro 需要额外的图片卡片偏移量
  const collapsedImageStartOffset = media.isQ2ProSeparatedMediaAreas
    ? // Q2Pro: 视频 + 间距 + 分隔线 + 间距
      DESKTOP_REFERENCE_STACK_COLLAPSED_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP +
      DESKTOP_REFERENCE_DIVIDER_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP
    : 0;

  const collapsedSubjectStartOffset = media.isQ2ProSeparatedMediaAreas
    ? // Q2Pro: 视频 + 间距 + 分隔线 + 间距 + 图片 + 间距 + 分隔线 + 间距
      DESKTOP_REFERENCE_STACK_COLLAPSED_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP +
      DESKTOP_REFERENCE_DIVIDER_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP +
      DESKTOP_REFERENCE_STACK_COLLAPSED_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP +
      DESKTOP_REFERENCE_DIVIDER_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP
    : // Q3: 图片 + 间距 + 分隔线 + 间距
      DESKTOP_REFERENCE_STACK_COLLAPSED_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP +
      DESKTOP_REFERENCE_DIVIDER_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP;

  // Q2Pro 图片卡片展开偏移（从折叠位置到展开位置的差值）
  const imageExpandedOffsetX = media.isQ2ProSeparatedMediaAreas
    ? // 展开后：视频展开宽度 + 间距 + 分隔线 + 间距
      // 折叠时：视频折叠宽度 + 间距 + 分隔线 + 间距
      // 差值 = (视频展开宽度 - 视频折叠宽度)
      q2ProVideoExpandedWidth +
      DESKTOP_REFERENCE_EXPANDED_SECTION_GAP +
      DESKTOP_REFERENCE_DIVIDER_WIDTH +
      DESKTOP_REFERENCE_STACK_GAP -
      collapsedImageStartOffset
    : undefined;

  const subjectExpandedOffsetX = shouldUseReferenceStackRotation
    ? media.isQ2ProSeparatedMediaAreas
      ? // Q2Pro: 展开后整体宽度 - 折叠时主体起始位置
        q2ProVideoExpandedWidth +
        DESKTOP_REFERENCE_EXPANDED_SECTION_GAP +
        DESKTOP_REFERENCE_DIVIDER_WIDTH +
        DESKTOP_REFERENCE_STACK_GAP +
        q2ProImageExpandedWidth +
        DESKTOP_REFERENCE_EXPANDED_SECTION_GAP +
        DESKTOP_REFERENCE_DIVIDER_WIDTH +
        DESKTOP_REFERENCE_STACK_GAP -
        collapsedSubjectStartOffset
      : // Q3: 图片展开宽度 + 间距 + 分隔线 + 间距 - 折叠时主体起始位置
        imageExpandedStackWidth +
        DESKTOP_REFERENCE_EXPANDED_SECTION_GAP +
        DESKTOP_REFERENCE_DIVIDER_WIDTH +
        DESKTOP_REFERENCE_STACK_GAP -
        collapsedSubjectStartOffset
    : undefined;

  const expandedDividerOffsetX = shouldUseReferenceStackRotation
    ? media.isQ2ProSeparatedMediaAreas
      ? // Q2Pro 第二条分隔线: 视频展开 + 间距 + 分隔线 + 间距 + 图片展开 + 间距
        q2ProVideoExpandedWidth +
        DESKTOP_REFERENCE_EXPANDED_SECTION_GAP +
        DESKTOP_REFERENCE_DIVIDER_WIDTH +
        DESKTOP_REFERENCE_STACK_GAP +
        q2ProImageExpandedWidth +
        DESKTOP_REFERENCE_EXPANDED_SECTION_GAP
      : imageExpandedStackWidth + DESKTOP_REFERENCE_EXPANDED_SECTION_GAP
    : undefined;
  const referenceHoverBridgeLeft = shouldRenderReferenceHoverBridge
    ? media.isQ2ProSeparatedMediaAreas
      ? // Q2Pro: 从第一个有内容的卡片展开后开始
        q2ProVideoChips.length > 0
        ? q2ProVideoExpandedWidth
        : q2ProImageChips.length > 0
          ? q2ProImageExpandedWidth
          : 0
      : imageExpandedStackWidth
    : undefined;
  const referenceHoverBridgeWidth = shouldRenderReferenceHoverBridge
    ? media.isQ2ProSeparatedMediaAreas
      ? // Q2Pro: 覆盖展开后第一个卡片之后的间隙及后续卡片区域。
        (() => {
          const sectionGap =
            DESKTOP_REFERENCE_EXPANDED_SECTION_GAP + DESKTOP_REFERENCE_DIVIDER_WIDTH + DESKTOP_REFERENCE_STACK_GAP;
          const expandedSectionWidths = [
            q2ProVideoExpandedWidth,
            q2ProImageExpandedWidth,
            q2ProSubjectExpandedWidth,
          ].filter((width) => width > 0);
          const expandedContentWidth =
            expandedSectionWidths.reduce((total, width) => total + width, 0) +
            Math.max(0, expandedSectionWidths.length - 1) * sectionGap;

          return Math.max(0, expandedContentWidth - (referenceHoverBridgeLeft ?? 0));
        })()
      : DESKTOP_REFERENCE_EXPANDED_SECTION_GAP + DESKTOP_REFERENCE_DIVIDER_WIDTH + DESKTOP_REFERENCE_STACK_GAP
    : undefined;

  useEffect(() => {
    if (!shouldUseReferenceStackRotation) {
      setReferenceStacksExpanded(false);
    }
  }, [shouldUseReferenceStackRotation]);

  if (allMediaChips.length === 0 && (media.visibleMediaInputs?.length ?? 0) === 0) {
    return null;
  }

  return (
    <div
      data-reference-stacks-expanded={referenceStacksExpanded}
      className={cn(
        'relative flex items-start gap-3',
        shouldUseReferenceStackRotation &&
          'group/reference z-0 focus-within:z-20 data-[reference-stacks-expanded=true]:z-20',
        multiFrame.isMultiFrameMode ? 'min-w-0 flex-wrap' : 'shrink-0',
      )}
      style={
        shouldUseReferenceStackRotation
          ? ({
              '--reference-image-expanded-offset':
                imageExpandedOffsetX !== undefined ? `${imageExpandedOffsetX}px` : undefined,
              '--reference-subject-expanded-offset': `${subjectExpandedOffsetX}px`,
              '--reference-expanded-divider-offset': `${expandedDividerOffsetX}px`,
              ...(shouldRenderReferenceHoverBridge
                ? {
                    '--reference-hover-bridge-left': `${referenceHoverBridgeLeft}px`,
                    '--reference-hover-bridge-width': `${referenceHoverBridgeWidth}px`,
                  }
                : {}),
            } as CSSProperties)
          : undefined
      }
      onPointerLeave={(event) => {
        // 加号下拉挂在 portal 上，指针移过去就算离开了这里；此时收起会让加号锚点透明化。
        if (addMenuOpen) return;
        // 展开后的卡片会溢出固定布局宽度，但仍是当前容器的后代；移动到这些卡片时不要收起。
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        setReferenceStacksExpanded(false);
      }}
    >
      {shouldRenderReferenceHoverBridge ? (
        <div
          aria-hidden={true}
          className="pointer-events-auto absolute top-0 left-[var(--reference-hover-bridge-left)] z-0 h-[82px] w-[var(--reference-hover-bridge-width)] bg-transparent"
          onPointerEnter={() => setReferenceStacksExpanded(true)}
        />
      ) : null}
      {multiFrame.isMultiFrameMode ? (
        <HomeGenerationMultiFrameRail controller={controller} />
      ) : media.isQ2ProSeparatedMediaAreas ? (
        // Q2Pro: 固定容器 + absolute wrapper + flex 布局
        <div
          className="relative shrink-0"
          style={{
            // 折叠时的宽度：3个卡片(64px) + 2个分隔线间隙(12+4+12)
            width: `${3 * 64 + 2 * (12 + 4 + 12)}px`,
          }}
        >
          {/* Absolute wrapper - 允许展开时内容溢出到右边 */}
          <div className="absolute top-0 left-0 h-[82px] overflow-visible">
            {/* Flex container - 自动布局卡片和分隔线 */}
            <div className="flex items-start gap-3" onPointerEnter={() => setReferenceStacksExpanded(true)}>
              <HeadlessVideoUploader
                disabled={!media.canUploadReferenceVideo && q2ProVideoChips.length === 0}
                minDuration={media.referenceVideoConfig.minVideoDuration}
                maxDuration={media.referenceVideoConfig.maxVideoDuration}
                maxSize={media.referenceVideoConfig.maxVideoSize}
                captureCoverImage={true}
                uploadCoverImage={true}
                allowTempMode={true}
                emitUploadingPreview={true}
                onChange={media.handleMobileMediaOptionVideoChange}
              >
                {({ openFileSelector }) => (
                  <HomeGenerationDesktopMediaArea
                    chips={q2ProVideoChips}
                    title={t('home.generationForm.actions.video')}
                    emptyIcon={<ReferenceMaterial className="size-5" />}
                    emptyContent={
                      <DropdownMenu onOpenChange={setAddMenuOpen}>
                        <DropdownMenuTrigger asChild={true}>
                          <button
                            type="button"
                            className="flex h-[82px] w-16 flex-col items-center justify-center gap-1.5 text-system-white64 transition-colors hover:text-system-white disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={!media.canUploadReferenceVideo}
                          >
                            <ReferenceMaterial className="size-5" />
                            <span className="max-w-full truncate px-1 text-center text-xs">
                              {t('home.generationForm.actions.reference')}
                            </span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="bottom"
                          align="center"
                          sideOffset={8}
                          collisionPadding={16}
                          className="z-[41] w-[100px] gap-0 overflow-hidden rounded-12 border border-white/[0.08] bg-[rgba(23,25,28,0.64)] p-0 text-system-white shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur-[30px]"
                        >
                          <DropdownMenuItem
                            disabled={!media.canUploadReferenceVideo}
                            className="flex h-[40px] items-center justify-center gap-2 rounded-none px-3 text-system-white64 text-xs hover:bg-white/[0.08] hover:text-system-white"
                            onSelect={() => {
                              media.handleVideoUploadClick();
                              setTimeout(() => void openFileSelector(), 0);
                            }}
                          >
                            <Video className="size-4" />
                            {t('home.generationForm.actions.video')}
                          </DropdownMenuItem>
                          <div aria-hidden={true} className="h-px bg-white/[0.12]" />
                          <DropdownMenuItem
                            disabled={!media.canUploadReferenceVideo}
                            className="flex h-[40px] items-center justify-center gap-2 rounded-none px-3 text-system-white64 text-xs hover:bg-white/[0.08] hover:text-system-white"
                            onSelect={() => media.handleSelectSubject(['video'])}
                          >
                            <ProductLineSubject className="size-4" />
                            {t('home.generationForm.actions.subject')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    }
                    disabled={!media.canUploadReferenceVideo && q2ProVideoChips.length === 0}
                    canAdd={q2ProVideoChips.length > 0 && media.canUploadReferenceVideo}
                    addMenuItems={[
                      {
                        key: 'video',
                        icon: <Video className="size-4" />,
                        label: t('home.generationForm.actions.video'),
                        disabled: !media.canUploadReferenceVideo,
                        onSelect: () => {
                          media.handleVideoUploadClick();
                          setTimeout(() => void openFileSelector(), 0);
                        },
                      },
                      {
                        key: 'subject',
                        icon: <ProductLineSubject className="size-4" />,
                        label: t('home.generationForm.actions.subject'),
                        disabled: !media.canUploadReferenceVideo,
                        onSelect: () => media.handleSelectSubject(['video']),
                      },
                    ]}
                    addDisabled={!media.canUploadReferenceVideo}
                    onAddMenuOpenChange={setAddMenuOpen}
                    stackExpanded={shouldUseReferenceStackRotation ? referenceStacksExpanded : undefined}
                    onStackExpandedChange={shouldUseReferenceStackRotation ? setReferenceStacksExpanded : undefined}
                  />
                )}
              </HeadlessVideoUploader>

              <div aria-hidden={true} className="mt-[29px] h-6 w-px shrink-0 bg-white opacity-[0.16]" />

              <HeadlessImageUploader
                maxCount={media.remainingImageUploadCount}
                disabled={!canUploadImage}
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize: media.imageUploadMaxImageSize })}
                onChange={media.handleUploadImage}
                allowTempMode={true}
                emitUploadingPreview={true}
              >
                {({ openFileSelector }) => (
                  <HomeGenerationDesktopMediaArea
                    chips={q2ProImageChips}
                    title={t('home.generationForm.actions.image')}
                    emptyIcon={<ProductLineImage className="size-5" />}
                    emptyTitle={t('home.generationForm.actions.upload')}
                    showEmptyTitle={true}
                    onClick={openFileSelector}
                    disabled={!canUploadImage && q2ProImageChips.length === 0}
                    canAdd={q2ProImageChips.length > 0 && canUploadImage}
                    onAddClick={openFileSelector}
                    addDisabled={!canUploadImage}
                    hideCollapsedShadow={true}
                    stackExpanded={shouldUseReferenceStackRotation ? referenceStacksExpanded : undefined}
                    onStackExpandedChange={shouldUseReferenceStackRotation ? setReferenceStacksExpanded : undefined}
                  />
                )}
              </HeadlessImageUploader>

              <div aria-hidden={true} className="mt-[29px] h-6 w-px shrink-0 bg-white opacity-[0.16]" />

              <HomeGenerationDesktopMediaArea
                chips={q2ProSubjectChips}
                title={t('home.generationForm.actions.subject')}
                emptyIcon={<ProductLineSubject className="size-5" />}
                emptyContent={
                  <button
                    type="button"
                    className="flex h-[82px] w-16 flex-col items-center justify-center gap-1.5 text-system-white64 transition-colors hover:text-system-white"
                    disabled={controller.form.imageAndTextReferenceCount >= controller.form.maxImageAndElementCount}
                    onClick={() => media.handleSelectSubject(['text', 'image'])}
                  >
                    <ProductLineSubject className="size-5" />
                    <span className="max-w-full truncate px-1 text-center text-xs">
                      {t('home.generationForm.actions.subject')}
                    </span>
                  </button>
                }
                onClick={q2ProSubjectChips.length > 0 ? () => media.handleSelectSubject(['text', 'image']) : undefined}
                hideCollapsedShadow={true}
                disabled={
                  q2ProSubjectChips.length === 0 &&
                  controller.form.imageAndTextReferenceCount >= controller.form.maxImageAndElementCount
                }
                canAdd={
                  q2ProSubjectChips.length > 0 &&
                  controller.form.imageAndTextReferenceCount < controller.form.maxImageAndElementCount
                }
                onAddClick={() => media.handleSelectSubject(['text', 'image'])}
                addDisabled={controller.form.imageAndTextReferenceCount >= controller.form.maxImageAndElementCount}
                clickableWithMedia={true}
                stackExpanded={shouldUseReferenceStackRotation ? referenceStacksExpanded : undefined}
                onStackExpandedChange={shouldUseReferenceStackRotation ? setReferenceStacksExpanded : undefined}
              />
            </div>
          </div>
        </div>
      ) : shouldShowImageMediaArea ? (
        <div>
          {media.shouldUseMixedReferenceMediaUpload ? (
            <HeadlessMediaUploader
              maxCount={media.mixedReferenceMediaMaxCount}
              maxAudioCount={media.canUploadReferenceAudio ? 3 : 0}
              minAudioDuration={media.canUploadReferenceAudio ? media.dropZone.minAudioDuration : undefined}
              maxAudioDuration={media.canUploadReferenceAudio ? media.dropZone.maxAudioDuration : undefined}
              maxAudioSize={media.canUploadReferenceAudio ? media.dropZone.maxAudioSize : undefined}
              audioAccept={media.referenceAudioAccept}
              maxImageCount={media.remainingImageUploadCount}
              maxImageFileSize={media.imageUploadMaxFileSize}
              maxVideoCount={media.canUploadReferenceVideo ? media.referenceVideoConfig.maxVideoCount : 0}
              disabled={!media.canUploadMixedReferenceMedia}
              acceptImage={media.remainingImageUploadCount > 0}
              acceptVideo={media.canUploadReferenceVideo}
              acceptAudio={media.canUploadReferenceAudio}
              getSelectedImage={(url) =>
                resizeSourceImage({
                  url,
                  maxImageSize: media.imageUploadMaxImageSize,
                })
              }
              minVideoDuration={media.referenceVideoConfig.minVideoDuration}
              maxVideoDuration={media.referenceVideoConfig.maxVideoDuration}
              maxVideoSize={media.referenceVideoConfig.maxVideoSize}
              captureCoverImage={true}
              uploadCoverImage={true}
              allowTempMode={true}
              emitUploadingPreview={true}
              onChange={media.handleUploadReferenceMedia}
            >
              {({ openFileSelector }) => (
                <HomeGenerationDesktopMediaArea
                  chips={mediaUploadChips}
                  title={media.mixedReferenceMediaActionLabel}
                  emptyIcon={getReferenceUploadEmptyIcon({
                    canUploadImage: media.remainingImageUploadCount > 0,
                    canUploadVideo: media.canUploadReferenceVideo,
                    canUploadAudio: media.canUploadReferenceAudio,
                  })}
                  emptyTitle={t('home.generationForm.actions.upload')}
                  showEmptyTitle={true}
                  onClick={() => {
                    setTimeout(() => {
                      void openFileSelector();
                    }, 0);
                  }}
                  disabled={!media.canUploadMixedReferenceMedia && mediaUploadChips.length === 0}
                  canAdd={mediaUploadChips.length > 0 && media.canUploadMixedReferenceMedia}
                  onAddClick={() => {
                    setTimeout(() => {
                      void openFileSelector();
                    }, 0);
                  }}
                  addDisabled={!media.canUploadMixedReferenceMedia}
                  hideCollapsedShadow={true}
                  expandedOffset="image"
                  stackExpanded={shouldUseReferenceStackRotation ? referenceStacksExpanded : undefined}
                  onStackExpandedChange={shouldUseReferenceStackRotation ? setReferenceStacksExpanded : undefined}
                />
              )}
            </HeadlessMediaUploader>
          ) : (
            <HeadlessImageUploader
              maxCount={media.remainingImageUploadCount}
              maxFileSize={media.imageUploadMaxFileSize}
              disabled={!canUploadImage}
              getSelectedImage={(url) =>
                resizeSourceImage({
                  url,
                  maxImageSize: media.imageUploadMaxImageSize,
                })
              }
              onChange={media.handleUploadImage}
              allowTempMode={true}
              emitUploadingPreview={true}
            >
              {({ openFileSelector }) => (
                <HomeGenerationDesktopMediaArea
                  chips={imageChips}
                  title={t('home.generationForm.actions.image')}
                  emptyIcon={getReferenceUploadEmptyIcon({ canUploadImage: true, canUploadVideo: false })}
                  emptyTitle={t('home.generationForm.actions.upload')}
                  showEmptyTitle={true}
                  onClick={openFileSelector}
                  disabled={!canUploadImage && imageChips.length === 0}
                  canAdd={imageChips.length > 0 && canUploadImage}
                  onAddClick={openFileSelector}
                  addDisabled={!canUploadImage}
                  hideCollapsedShadow={true}
                  expandedOffset="image"
                  stackExpanded={shouldUseReferenceStackRotation ? referenceStacksExpanded : undefined}
                  onStackExpandedChange={shouldUseReferenceStackRotation ? setReferenceStacksExpanded : undefined}
                />
              )}
            </HeadlessImageUploader>
          )}
        </div>
      ) : null}

      {!multiFrame.isMultiFrameMode &&
      !media.isQ2ProSeparatedMediaAreas &&
      shouldShowImageMediaArea &&
      shouldShowSubjectMediaArea ? (
        <>
          <div
            aria-hidden={true}
            className="pointer-events-none mt-[29px] h-6 w-px shrink-0 bg-white opacity-[0.16] group-data-[reference-stacks-expanded=true]/reference:opacity-0"
          />
          <div
            aria-hidden={true}
            className="pointer-events-none absolute top-[29px] left-0 z-10 h-6 w-px bg-white opacity-0 group-data-[reference-stacks-expanded=true]/reference:opacity-[0.16]"
            style={{ transform: 'translateX(var(--reference-expanded-divider-offset))' }}
          />
        </>
      ) : null}

      {!media.isQ2ProSeparatedMediaAreas && shouldShowSubjectMediaArea ? (
        <HomeGenerationDesktopMediaArea
          chips={subjectChips}
          title={t('home.generationForm.actions.subject')}
          emptyIcon={canSelectSubject ? <ProductLineSubject className="size-5" /> : <AtSign className="size-5" />}
          onClick={() => media.handleSelectSubject()}
          disabled={!canSelectSubject && subjectChips.length === 0}
          canAdd={subjectChips.length > 0 && canSelectSubject}
          onAddClick={() => media.handleSelectSubject()}
          addDisabled={!canSelectSubject}
          clickableWithMedia={true}
          showEmptyTitle={true}
          hideCollapsedShadow={true}
          expandedOffset="subject"
          stackExpanded={shouldUseReferenceStackRotation ? referenceStacksExpanded : undefined}
          onStackExpandedChange={shouldUseReferenceStackRotation ? setReferenceStacksExpanded : undefined}
        />
      ) : null}

      {!media.isQ2ProSeparatedMediaAreas && !media.shouldUseMixedReferenceMediaUpload && videoChips.length > 0 ? (
        <HeadlessVideoUploader
          disabled={!media.canUploadReferenceVideo}
          minDuration={media.referenceVideoConfig.minVideoDuration}
          maxDuration={media.referenceVideoConfig.maxVideoDuration}
          maxSize={media.referenceVideoConfig.maxVideoSize}
          captureCoverImage={true}
          uploadCoverImage={true}
          allowTempMode={true}
          emitUploadingPreview={true}
          onChange={media.handleMobileMediaOptionVideoChange}
        >
          {({ openFileSelector }) => (
            <HomeGenerationDesktopMediaArea
              chips={videoChips}
              title={t('home.generationForm.actions.video')}
              emptyIcon={<Video className="size-4" />}
              onClick={() => {
                media.handleVideoUploadClick();
                setTimeout(() => {
                  void openFileSelector();
                }, 0);
              }}
              disabled={!media.canUploadReferenceVideo && videoChips.length === 0}
              canAdd={videoChips.length > 0 && media.canUploadReferenceVideo}
              onAddClick={() => {
                media.handleVideoUploadClick();
                setTimeout(() => {
                  void openFileSelector();
                }, 0);
              }}
              addDisabled={!media.canUploadReferenceVideo}
            />
          )}
        </HeadlessVideoUploader>
      ) : null}
    </div>
  );
};

export const HomeGenerationMobileMediaOptions = ({ controller }: HomeGenerationMediaProps) => {
  const t = useTranslations();
  const { media } = controller;

  return (
    <div className="flex flex-col gap-2 text-system-white">
      {media.showMobileSubjectInput ? (
        <button
          type="button"
          className="flex h-12 items-center gap-3 rounded-10 bg-white/[0.06] px-3 text-left text-sm"
          onClick={() => {
            controller.dialogs.setMobileMediaOptionsOpen(false);
            media.handleSelectSubject();
          }}
        >
          <AtSign className="size-4 text-system-white64" />
          {t('home.generationForm.actions.subject')}
        </button>
      ) : null}
      {media.showMobileImageInput ? (
        media.shouldUseMixedReferenceMediaUpload ? (
          <HeadlessMediaUploader
            maxCount={media.mixedReferenceMediaMaxCount}
            maxAudioCount={media.canUploadReferenceAudio ? 3 : 0}
            minAudioDuration={media.canUploadReferenceAudio ? media.dropZone.minAudioDuration : undefined}
            maxAudioDuration={media.canUploadReferenceAudio ? media.dropZone.maxAudioDuration : undefined}
            maxAudioSize={media.canUploadReferenceAudio ? media.dropZone.maxAudioSize : undefined}
            audioAccept={media.referenceAudioAccept}
            maxImageCount={media.remainingImageUploadCount}
            maxImageFileSize={media.imageUploadMaxFileSize}
            maxVideoCount={media.canUploadReferenceVideo ? media.referenceVideoConfig.maxVideoCount : 0}
            disabled={!media.canUploadMixedReferenceMedia}
            acceptImage={media.remainingImageUploadCount > 0}
            acceptVideo={media.canUploadReferenceVideo}
            acceptAudio={media.canUploadReferenceAudio}
            getSelectedImage={(url) =>
              resizeSourceImage({
                url,
                maxImageSize: media.imageUploadMaxImageSize,
              })
            }
            minVideoDuration={media.referenceVideoConfig.minVideoDuration}
            maxVideoDuration={media.referenceVideoConfig.maxVideoDuration}
            maxVideoSize={media.referenceVideoConfig.maxVideoSize}
            captureCoverImage={true}
            uploadCoverImage={true}
            allowTempMode={true}
            emitUploadingPreview={true}
            onChange={media.handleMobileMediaOptionMediaChange}
          >
            {({ openFileSelector }) => (
              <button
                type="button"
                className="flex h-12 items-center gap-3 rounded-10 bg-white/[0.06] px-3 text-left text-sm disabled:cursor-not-allowed disabled:text-system-white24"
                onClick={() => {
                  setTimeout(() => {
                    void openFileSelector();
                  }, 0);
                }}
                disabled={!media.canUploadMixedReferenceMedia}
              >
                <ImageIcon className="size-4 text-system-white64" />
                {media.mixedReferenceMediaActionLabel}
              </button>
            )}
          </HeadlessMediaUploader>
        ) : (
          <HeadlessImageUploader
            maxCount={media.remainingImageUploadCount}
            maxFileSize={media.imageUploadMaxFileSize}
            disabled={media.remainingImageUploadCount <= 0}
            getSelectedImage={(url) =>
              resizeSourceImage({
                url,
                maxImageSize: media.imageUploadMaxImageSize,
              })
            }
            onChange={media.handleMobileMediaOptionImageChange}
            allowTempMode={true}
            emitUploadingPreview={true}
          >
            {({ openFileSelector }) => (
              <button
                type="button"
                className="flex h-12 items-center gap-3 rounded-10 bg-white/[0.06] px-3 text-left text-sm disabled:cursor-not-allowed disabled:text-system-white24"
                onClick={() => {
                  void openFileSelector();
                }}
                disabled={media.remainingImageUploadCount <= 0}
              >
                <ImageIcon className="size-4 text-system-white64" />
                {t('home.generationForm.actions.image')}
              </button>
            )}
          </HeadlessImageUploader>
        )
      ) : null}
    </div>
  );
};
